import type { CSSProperties } from 'react';
import { useEffect, useRef, useState } from 'react';

/* 전역 IntersectionObserver 객체 선언 */
let observer: IntersectionObserver | null = null;

/* 이미지 로드 이벤트 타입 상수 */
const LOAD_IMG_EVENT_TYPE = 'loadImage';

/* IntersectionObserver 콜백 함수 */
const onIntersection = (entries: IntersectionObserverEntry[], io: IntersectionObserver) => {
  entries.forEach((entry) => {
    if (entry.isIntersecting) {
      /* 요소가 뷰포트에 진입하면 관찰 중단 및 이미지 로드 이벤트 발생 */
      io.unobserve(entry.target);
      entry.target.dispatchEvent(new CustomEvent(LOAD_IMG_EVENT_TYPE));
    }
  });
};

/**
 * Image 컴포넌트 props 인터페이스 정의
 *
 * @interface ImageProps
 * @property {boolean} [lazy] - 지연 로딩 사용 여부
 * @property {number} [threshold] - IntersectionObserver 임계값
 * @property {boolean} [block] - 블록 레벨 요소로 표시할지 여부
 * @property {string} src - 이미지 소스 URL
 * @property {number | 'auto'} [width] - 이미지 너비
 * @property {number} [height] - 이미지 높이
 * @property {string} [alt] - 대체 텍스트
 * @property {'cover' | 'fill' | 'contain'} [mode] - 이미지 맞춤 모드
 * @property {string} [placeholder] - 로딩 중 표시할 플레이스홀더 이미지
 * @property {CSSProperties} [style] - 추가 스타일
 */
interface ImageProps {
  lazy?: boolean;
  threshold?: number;
  block?: boolean;
  src: string;
  width?: number | 'auto';
  height?: number;
  borderRadius?: number;
  alt?: string;
  mode?: 'cover' | 'fill' | 'contain';
  placeholder?: string;
  style?: CSSProperties;
}

export const Image = ({
  lazy,
  threshold = 0.5,
  placeholder,
  block,
  src,
  width,
  height,
  borderRadius = 0,
  alt,
  mode = 'cover',
  style,
}: ImageProps) => {
  /* 이미지 로드 상태 관리 */
  const [loaded, setLoaded] = useState(false);

  /* 이미지 요소에 대한 ref */
  const imgRef = useRef<HTMLImageElement>(null);

  /* 기본 이미지 스타일 설정 */
  const imageStyle = {
    display: block ? 'block' : undefined,
    width: width === undefined ? '100%' : width,
    minWidth: width === undefined ? '100%' : width,
    height: height === undefined ? '100%' : height,
    objectFit: mode,
    borderRadius: `${borderRadius}px`,
    opacity: loaded ? 1 : 0,
    transition: 'opacity 0.1s ease-in-out',
  };

  useEffect(() => {
    if (!lazy) {
      /* 지연 로딩이 비활성화된 경우 즉시 로드 상태로 설정 */
      setLoaded(true);
      return;
    }

    const handleLoadImage = () => setLoaded(true);

    const imgElement = imgRef.current;
    if (imgElement) {
      /* 이미지 로드 이벤트 리스너 등록 */
      imgElement.addEventListener(LOAD_IMG_EVENT_TYPE, handleLoadImage);
    }

    return () => {
      if (imgElement) {
        /* 컴포넌트 언마운트 시 이벤트 리스너 제거 */
        imgElement.removeEventListener(LOAD_IMG_EVENT_TYPE, handleLoadImage);
      }
    };
  }, [lazy]);

  useEffect(() => {
    if (!lazy) {
      return;
    }

    /* IntersectionObserver 생성 및 설정 */
    observer = new IntersectionObserver(onIntersection, { threshold });

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }
  }, [lazy, threshold]);

  return (
    <img
      ref={imgRef}
      src={loaded ? src : placeholder}
      alt={alt}
      style={{
        ...style,
        ...imageStyle,
      }}
    />
  );
};
